import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  Inject,
  ElementRef,
} from '@angular/core';
import * as L from 'leaflet';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OPERATORS, RF_RADIUS } from 'src/app/services/constants';
import { ApiService } from 'src/app/services/api.service';
import * as moment from 'moment';
import { CollectionPopupComponent } from '../collections/collection-popup/collection-popup.component';
import { PopupService } from 'src/app/services/popup.service';
import { Subscription } from 'rxjs';
var turf = require('@turf/turf');
var geoParse = require('wellknown');

@Component({
  selector: 'app-leaflet-user-previescreen',
  templateUrl: './leaflet-user-previescreen.component.html',
  styleUrls: ['./leaflet-user-previescreen.component.scss'],
})
export class LeafletUserPreviescreenComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  map!: L.Map;
  featureGroup?: L.FeatureGroup;
  geojsonLayer?: L.GeoJSON<any>;
  dataLoading: boolean = true;
  satelliteDataSubscription?: Subscription;

  // Inputs / state
  taskTarget: any;
  taskid: any;
  taskname: any;
  coordinates: any = [];
  observationCoordinates: any = [];
  observationCoordinatesDisplay: any = [];
  sensors: any;
  targetPoints: any;
  observationLocation: any;
  observationType: any;
  cordinateLabel: string = 'Coordinates';
  initialBounds: any;
    latlonOverlay = '';

  // default view
  private defaultCenter: L.LatLngExpression = [25.3176, 82.9739];
  private defaultZoom = 2;

    streetLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors'
    });
  
    sentinelLayer = L.tileLayer(
      'https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2020_3857/default/g/{z}/{y}/{x}.jpg',
      {
        maxZoom: 19,
        tileSize: 256,
        attribution:
          '© <a href="https://scihub.copernicus.eu/">Copernicus Sentinel</a> | © EOX'
      }
    );

  constructor(
    private dialogRef: MatDialogRef<LeafletUserPreviescreenComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private apiService: ApiService,
    private popupService: PopupService,
    private el: ElementRef
  ) {
    if (data['type'] === 'taskPreview') {
      this.taskid = data['taskid'];
      this.taskname = data['taskname'];
      try {
        this.taskTarget = JSON.parse(this.data['target']);
      } catch {
        this.taskTarget = this.data['target'];
      }
      this.sensors = data['sensors'];
      this.targetPoints = this.data['targetPoints']
        ? JSON.parse(this.data['targetPoints'])
        : null;
      this.observationLocation = this.data['observationLocation'];

      this.coordinates = [];
      if (this.taskTarget) {
        if (this.taskTarget.type === 'Polygon') {
          this.coordinates = this.taskTarget.coordinates[0]
            .toString()
            .split(',')
            .map(Number);
        } else if (this.taskTarget?.length > 1) {
          this.taskTarget.type = this.taskTarget[0].type;
          for (const points of this.taskTarget) {
            this.coordinates.push(points.coordinates);
          }
        } else {
          this.coordinates = this.taskTarget.coordinates;
        }
      }
    }

    if (this.observationLocation) {
      const observationCoordinatesSplit = this.observationLocation.includes(';')
        ? this.observationLocation.split(';')
        : this.observationLocation;
      this.observationCoordinatesDisplay = observationCoordinatesSplit
        .toString()
        .split(',')
        .map(Number);
      this.observationCoordinates = this.observationLocation.split(';');
      this.observationType =
        this.observationCoordinates.length === 1 ? 'Point' : 'Polygon';
      this.cordinateLabel = 'Tipping Coordinates';
    }
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initLeaflet();

      if (this.data['type'] === 'taskPreview') this.plotTarget();
      else if (this.data['type'] === 'permittedArea')
        this.plotArea(this.data['user']?.taskableAoi || []);
      else if (this.data['type'] === 'satelliteTracking')
        this.getSatelliteData();

      this.map.invalidateSize();
    }, 0);
    this.initialBounds = [
      [-40, -20],
      [40, 20],
    ];
  }

  ngOnDestroy(): void {
    if (this.satelliteDataSubscription)
      this.satelliteDataSubscription.unsubscribe();
    if (this.map) this.map.remove();
  }

  initLeaflet() {
    const container = this.el.nativeElement.querySelector('#leafletContainer');

    this.map = L.map(container, {
      zoomControl: true,
      preferCanvas: true,
      fadeAnimation: true,
      zoomAnimation: true,
      markerZoomAnimation: true,
      layers: [this.sentinelLayer],
    }).setView([20, 78], 4);
    this.featureGroup = L.featureGroup().addTo(this.map);

    (this.map as any).getPane('popupPane').style.zIndex = 5000;
    (this.map as any).getPane('tooltipPane').style.zIndex = 4900;

    this.map.on('mousemove', (e: any) => {
      this.latlonOverlay = `Lat: ${e.latlng.lat.toFixed(5)}, Lng: ${e.latlng.lng.toFixed(5)}`;
    });

    const baseMaps = {
      'OpenStreetMap': this.streetLayer,
      'Sentinel-2 Cloudless': this.sentinelLayer
    };

    const layerControl = L.control.layers(baseMaps, {}, { collapsed: true }).addTo(this.map);

    setTimeout(() => {
      const btn = L.DomUtil.create('div', 'custom-layer-btn leaflet-bar');
      btn.innerHTML = `<img src="assets/icons/layers.png" style="width:30px; padding:4px; cursor:pointer;">`;

      L.DomEvent.disableClickPropagation(btn);

      L.DomEvent.on(btn, 'click', () => {
        const lc = document.querySelector('.leaflet-control-layers') as HTMLElement;
        lc.classList.toggle('leaflet-control-layers-expanded');
      });

      const topRight = document.querySelector('.leaflet-top.leaflet-right') as HTMLElement;
      topRight.appendChild(btn);
    }, 300);

    setTimeout(() => this.map.invalidateSize(true), 500);

    this.sentinelLayer.on('tileerror', () => {
      if (this.map.hasLayer(this.sentinelLayer)) {
        this.map.removeLayer(this.sentinelLayer);
        this.map.addLayer(this.streetLayer);
      }
    });
  }

  viewHome() {
    this.map.fitBounds(this.initialBounds);
  }

  removeHighlight() {
    const homeBtn = document.querySelector('.leaflet-control.home-control');
    if (homeBtn) homeBtn.classList.remove('highlight-border');
    const layerBtn = document.querySelector('.leaflet-control-layers-toggle');
    if (layerBtn) layerBtn.classList.remove('highlight-border');
  }

  plotTarget() {
    if (!this.map) return;

    if (this.taskTarget) {
      if (this.taskTarget.type === 'Polygon') {
        const coords = this.taskTarget.coordinates[0].map(([lng, lat]: any) => [
          lat,
          lng,
        ]);
        const poly = L.polygon(coords, {
          color: 'yellow',
          weight: 2,
          fillOpacity: 0.08,
        }).addTo(this.featureGroup!);
      } else if (Array.isArray(this.taskTarget) && this.taskTarget.length > 1) {
        this.createGeoJSONFile(this.taskTarget);
      } else if (this.taskTarget.type === 'Point') {
        const [lng, lat] = this.taskTarget.coordinates;
        const marker = L.marker([lat, lng]).addTo(this.featureGroup!);
      }
    }

    // Add targetPoints circles if provided
    if (this.targetPoints && Array.isArray(this.targetPoints)) {
      this.targetPoints.forEach((pt: any) => {
        if (pt && pt.length >= 2) {
          const circle = L.circle([pt[1], pt[0]], {
            radius: RF_RADIUS * 1000,
            color: '#0b62f6',
            fillColor: '#0b62f6',
            fillOpacity: 0.12,
            weight: 1,
          }).addTo(this.featureGroup!);
        }
      });
    }

    // observations
    if (this.observationCoordinates?.length > 0) {
      if (this.observationType === 'Point') {
        const [lng, lat] = this.observationCoordinates[0]
          .split(',')
          .map(Number);
        L.circleMarker([lat, lng], {
          radius: 6,
          color: 'red',
          fillColor: 'red',
          fillOpacity: 1,
        }).addTo(this.featureGroup!);
      } else if (this.observationType === 'Polygon') {
        const coords = this.observationCoordinates.map((c: string) => {
          const parts = c.split(',').map(Number);
          return [parts[1], parts[0]];
        });
        L.polygon(coords, { color: 'red', weight: 2, fillOpacity: 0.05 }).addTo(
          this.featureGroup!
        );
      }
    }

    // fit bounds once
    if (this.featureGroup && this.featureGroup.getLayers().length > 0) {
      const bounds = this.featureGroup.getBounds();
      if (bounds.isValid()) this.map.fitBounds(bounds.pad(0.15));
    }
  }

  createGeoJSONFile(features: any[]) {
    if (!Array.isArray(features) || features.length === 0) return;
    const geoJSON: any = {
      type: 'FeatureCollection',
      features: features.map((feature) => ({
        type: 'Feature',
        geometry: {
          type: feature.type,
          coordinates: feature.coordinates,
        },
      })),
    };
    // remove old geojson
    if (this.geojsonLayer) {
      this.map.removeLayer(this.geojsonLayer);
      this.geojsonLayer = undefined;
    }
    this.geojsonLayer = L.geoJSON(geoJSON, {
      style: { color: 'yellow', weight: 2, fillOpacity: 0.08 },
      onEachFeature: (_f, layer) => {
        layer.addTo(this.featureGroup!);
      },
    }).addTo(this.map);
  }

  plotArea(taskableAoi: any[]) {
    if (!Array.isArray(taskableAoi)) return;
    for (const file of taskableAoi) {
      try {
        const tt =
          typeof file === 'string'
            ? JSON.parse(file)
            : file.taskableAoi || file;
        if (!tt || tt.type !== 'Polygon') continue;

        const coords = tt.coordinates[0].map(([lng, lat]: any) => [lat, lng]);
        const poly = L.polygon(coords, {
          color: 'yellow',
          fillOpacity: 0.1,
        }).addTo(this.featureGroup!);

        // label at bottom-most point
        const polygonPoints = tt.coordinates[0];
        const bottomLonLat = polygonPoints.reduce(
          (lowest: any, current: any) =>
            current[1] < lowest[1] ? current : lowest,
          polygonPoints[0]
        );
        const latitudes = polygonPoints.map((p: any) => p[1]);
        const maxLat = Math.max(...latitudes);
        const minLat = Math.min(...latitudes);
        const latRange = maxLat - minLat;
        const shiftDegrees = latRange * 0.05;
        const labelLat = bottomLonLat[1] - shiftDegrees;
        const label = L.marker([labelLat, bottomLonLat[0]], {
          icon: L.divIcon({
            className: 'polygon-label',
            html: `<div style="background:rgba(0,0,0,0.5);color:white;padding:2px 6px;border-radius:4px;">${
              file.fileName || file.file
            }</div>`,
            iconSize: [100, 24],
          }),
        }).addTo(this.featureGroup!);
      } catch (e) {}
    }

    if (this.featureGroup && this.featureGroup.getLayers().length > 0) {
      const bounds = this.featureGroup.getBounds();
      if (bounds.isValid()) this.map.fitBounds(bounds.pad(0.15));
    }
  }

  getSatelliteData() {
    const sensorObj = {
      sensorData: {
        sensorName: [this.data?.sensor?.name || this.data?.sensor?.key],
        sensors: [{ key: this.data?.sensor?.name || this.data?.sensor?.key }],
      },
    };
    if (this.satelliteDataSubscription)
      this.satelliteDataSubscription.unsubscribe();
    this.satelliteDataSubscription = this.apiService
      .getSatelliteData(sensorObj)
      .subscribe((res: any) => {
        if (res?.msg === 'Failed') {
          this.dataLoading = false;
          const message =
            'There are no active satellites for the chosen provider at this time. Please select another.';
          this.popupService.openPopup(
            CollectionPopupComponent,
            message,
            '',
            '40%'
          );
        } else {
          this.plotSatellitePath(res);
        }
      });
  }

  plotSatellitePath(result: any) {
    if (!Array.isArray(result) || result.length === 0) return;
    const satGroup = L.featureGroup().addTo(this.featureGroup!);

    for (let i = 0; i < result.length; i++) {
      const satelliteData = result[i];
      const lat = satelliteData.latitude,
        lon = satelliteData.longitude;

      const marker = L.marker([lat, lon], {
        icon: L.icon({
          iconUrl: 'assets/images/satellite.png',
          iconSize: [25, 20],
          iconAnchor: [12.5, 20],
        }),
      }).addTo(satGroup);

      const labelText =
        (this.data['user']?.obfuscated && satelliteData.obfuscateName
          ? satelliteData.obfuscateName
          : satelliteData.name) +
        '<br>' +
        (satelliteData.time || '');
      L.marker([lat, lon], {
        icon: L.divIcon({
          className: 'satellite-label',
          html: `<div>${labelText}</div>`,
          iconSize: [130, 40],
          iconAnchor: [50, 20],
        }),
      }).addTo(satGroup);

      const posArr: number[][] = Array.isArray(satelliteData.position)
        ? satelliteData.position
        : [];
      if (posArr.length > 0) {
        const endLat = posArr[posArr.length - 1][1];
        const endLon = posArr[posArr.length - 1][0];
        const line = L.polyline(
          [
            [lat, lon],
            [endLat, endLon],
          ],
          { color: this.getOperatorColor(this.data['sensor']?.key), weight: 2 }
        ).addTo(satGroup);
      }
    }

    if (satGroup.getBounds().isValid()) {
      this.map.fitBounds(satGroup.getBounds().pad(0.15));
    }

    this.dataLoading = false;
  }

  getOperatorColor(key: string): string {
    switch (key) {
      case OPERATORS.CAPELLA:
        return 'yellow';
      case OPERATORS.SATELLOGIC:
        return 'yellowgreen';
      case OPERATORS.UMBRA:
        return 'blue';
      case OPERATORS.HEAD:
        return 'greenyellow';
      case OPERATORS.STE:
        return 'blueviolet';
      case OPERATORS.AT21:
        return 'green';
      case OPERATORS.KOMPSAT:
        return 'cyan';
      case OPERATORS.USL:
        return 'indigo';
      case OPERATORS.ISI:
        return 'turquoise';
      case OPERATORS.AXELSPACE:
        return 'gold';
      case OPERATORS.GHG:
        return '#25aae0';
      case OPERATORS.ORORATECH:
        return '#008B8B';
      case OPERATORS.WYVERN:
        return 'brown';
      case OPERATORS.SPIRE:
        return 'plum';
      case OPERATORS.CLYDE:
        return '#BC8F8F';
      case OPERATORS.SYNSPECTIVE:
        return 'green';
      case OPERATORS.GOKTURK:
        return '#9ce49c';
      case OPERATORS.LBAND:
        return 'violet';
      case OPERATORS.ECURS:
      case OPERATORS.ECURS_L:
        return 'aqua';
      case OPERATORS.ESPER:
        return 'deeppink';
      default:
        return 'royalblue';
    }
  }

  onKeyPress(e: any, msg: string) {
    if (e.key === 'Enter') {
      this.onNoClick(msg);
    }
  }
  onNoClick(msg: any): void {
    this.dialogRef.close('Done');
  }
}
